/* eslint-disable react/no-array-index-key */
/* eslint-env browser */

import styled from '@emotion/styled';
import { MutableState } from 'final-form';
import { Box, ScrollIntoView } from 'localmed-core';
import compose from 'lodash/flowRight';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { graphql } from 'react-apollo';
import { Form } from 'react-final-form';

import { CREATE_APPOINTMENT_MUTATION } from '../../../../../shared/components/AppointmentForm/AppointmentFormWithData';
import { DEPENDENT_STATUS } from '../../../../../shared/components/AppointmentForm/constants';
import { saveNewUserSession } from '../../../../../shared/components/AppointmentForm/newUserSession';
import submitAppointment from '../../../../../shared/components/AppointmentForm/submitAppointment';
import WIDGET_SESSION_SCHEDULED_MUTATION from '../../../../../shared/components/AppointmentForm/WidgetSessionScheduledMutation.graphql';
import {
  APPOINTMENT_STATUS,
  DILM_CLIENT_CONTEXT,
  FLASH_MESSAGES,
  LM_CLIENT_CONTEXT,
  OTHER_ID,
} from '../../../../../shared/constants';
import { buildAppointmentUrl } from '../../../../../shared/utils/urls';
import type { IUniversalContext } from '../../../../../types';
import { UniversalContext } from '../../../../../_universal';
import markRequestAsScheduled from '../../utils';
import MARK_REQUEST_AS_SCHEDULES from './markRequestAsScheduled.graphql';
import { IState, IWizardController, Response } from './types';

const Container = styled(Box)`
  min-height: 100%;
`;

function SchedulingWizard(props: any) {
  const { trackEvent, trackError }: IUniversalContext = useContext<any>(UniversalContext);
  const submitCountRef = useRef(0);
  const [values, setValues] = useState<IState>({
    step: 0,
    maxStep: 0,
    lastKnownValues: props.initialValues || {},
  });

  const getSteps = () =>
    React.Children.toArray(props.children).filter((component) => component !== null);

  const getCurrentStep = () => {
    const { step } = values;
    const steps = getSteps();
    const currentStep = steps[step];
    if (!currentStep) {
      throw new Error(`Could not find a component for step ${step}`);
    }
    return currentStep;
  };

  const getStepResult = async (wizardStep, currentValues) => {
    let { completed } = wizardStep.props;

    if (typeof completed === 'function') {
      try {
        completed = await completed(currentValues);
      } catch (error) {
        completed = false;
      }
    }
    return { completed: !!completed };
  };

  const canGotoStep = (step) => step >= 0 && step <= values.maxStep;

  const gotoStep = (step) => {
    if (!canGotoStep(step)) {
      throw new Error(`Cannot goto step ${step}`);
    }
    setValues((currValues) => ({ ...currValues, step }));
  };

  const gotoPreviousStep = () => {
    gotoStep(values.step - 1);
  };

  const gotoNextStep = () => {
    gotoStep(values.step + 1);
  };

  const isFirstPage = () => values.step === 0;

  const isLastPage = () =>
    values.step ===
    React.Children.toArray(props.children).filter((component) => component !== null).length - 1;

  const getFormError = (form) => {
    if (submitCountRef.current === 0 || form.isSubmitting) {
      return null;
    }
    const { status = {}, isValid } = form;
    const unhandledServerErrors = omit(status.errors || {}, [
      'username',
      'opening',
      'contactPhone',
      'patientCoverage.insurancePhone',
      'patientCoverage.postalCode',
      'patientCoverage.ssn',
    ]);
    const hasUnhandledServerErrors =
      status.errors == null || Object.keys(unhandledServerErrors).length > 0;
    if (status.isSuccessful === false && hasUnhandledServerErrors) {
      // If there are any fields we don't specifically handle in the UI, we’ll display
      // the generic error message to the user.
      form.change(
        'submissionErrorMessage',
        'We’re sorry, the appointment could not be scheduled because of an error. We’ve been notified and are working to correct the issue. Please try again in a few minutes.'
      );
    }
    if (!isValid) {
      return 'Please correct the errors in the form and submit again.';
    }
    return null;
  };

  const onSubmit = async (formValues, form) => {
    const response: Response = await submitAppointment(formValues, props);

    const { appointment } = response;
    // Using a temp value because operator assignment is not working
    const tempCount = submitCountRef.current;
    submitCountRef.current = tempCount + 1;

    // form.setSubmitting(false);
    form.change('appointment', appointment);
    form.change('serverErrors', response.errors);
    // form.setStatus(response);

    // Track events
    const submittedAppointmentEvent = {
      event: 'Submitted Appointment',
      properties: {
        ...pick(response, ['isSuccessful', 'status', 'message', 'errors']),
        submitCount: submitCountRef.current,
      },
    };
    trackEvent(submittedAppointmentEvent);

    if (response.isSuccessful) {
      if (formValues.createAccount) {
        saveNewUserSession(response);
      }
      if (appointment) {
        const {
          scheduleRequest,
          contactMobilePhone,
          reasonForVisit,
          insuranceIssuer,
          dependentStatus,
          createAccount,
          provider,
          office,
          practice,
          patientStatus,
          history,
        } = formValues;

        const isForDependent = dependentStatus === DEPENDENT_STATUS.DEPENDENT;
        const scheduledAppointmentEvent = {
          event: 'Scheduled Appointment',
          properties: {
            contactMobilePhone,
            appointmentId: appointment?.id,
            providerId: provider.id,
            providerDisplay: provider.display,
            officeId: office.id,
            officeDisplay: office.display,
            practiceId: practice.id,
            practiceDisplay: practice.display,
            reasonForVisitId: reasonForVisit.id,
            reasonForVisitDisplay: reasonForVisit.display,
            patientStatusId:
              typeof patientStatus === 'string' ? patientStatus : patientStatus?.id.toLowerCase(),
            insuranceIssuerId:
              insuranceIssuer && insuranceIssuer.id !== OTHER_ID ? insuranceIssuer.id : null,
            insuranceIssuerDisplay:
              insuranceIssuer && insuranceIssuer.id !== OTHER_ID ? insuranceIssuer.display : null,
            createdAccount: createAccount,
            createdDependent: createAccount && isForDependent,
            scheduledForDependent: isForDependent,
            submitCount: submitCountRef.current,
          },
        };
        trackEvent(scheduledAppointmentEvent);
        form.change('scheduledAppointmentEvent', scheduledAppointmentEvent);

        if (scheduleRequest) {
          const markRequestAsScheduledResponse = await markRequestAsScheduled(
            formValues,
            appointment.id,
            props
          );

          if (markRequestAsScheduledResponse.status === 'FAILED') {
            trackError({
              error: markRequestAsScheduledResponse.errors,
              component: 'SchedulingWizard',
            });
          }
        }

        const appointmentUrl = buildAppointmentUrl(provider.id, appointment.id, {
          patientToken: appointment.patientToken,
          params: {
            message:
              appointment.status === APPOINTMENT_STATUS.PENDING ? FLASH_MESSAGES.SCHEDULED : '',
            exp_widget_v2: true, // Used for the experimentation period
          },
        });

        if (appointment && appointment.status === APPOINTMENT_STATUS.PENDING && !scheduleRequest) {
          history.push(appointmentUrl);
        } else {
          gotoNextStep();
        }
      }
    } else {
      const { errors } = response;
      const validationError = getFormError(form);
      if (errors) {
        if (errors.opening) {
          form.change('openingError', true);
          gotoStep(1);
        } else if (validationError) {
          form.change('validationError', validationError);
          gotoStep(formValues.timeSelectionStep + 1); // GOTO patient Form step
        } else {
          form.change('appointmentMutationResponse', response);
        }
      } else {
        form.change('hasServerError', true);
      }
    }
  };

  const createWizardController = ({ form }): IWizardController => {
    const { step } = values;
    return {
      step,
      steps: getSteps(),
      gotoStep,
      gotoNextStep,
      hasPreviousStep: !isFirstPage(),
      previousButtonProps: {
        disabled: isFirstPage(),
        onClick: () => gotoPreviousStep(),
      },
      hasNextStep: !isLastPage(),
      nextButtonProps: {
        disabled: !canGotoStep(step + 1),
        onClick: () => gotoNextStep(),
      },
      formProps: form,
    };
  };

  const updateStepResults = async (formValues) => {
    const { step } = values;
    const steps = getSteps();

    const stepResults = await Promise.all(steps.map((s) => getStepResult(s, formValues)));

    const maxStep = stepResults.findIndex((stepResult) => !stepResult.completed);

    const currentStepProps: any = steps[step];

    if ((maxStep > values.maxStep || maxStep > step) && currentStepProps.props.autoStep) {
      setValues({ maxStep, step: step + 1, lastKnownValues: formValues });
    } else {
      setValues((currValues) => ({ ...currValues, maxStep, lastKnownValues: formValues }));
    }
  };

  useEffect(() => {
    if (props.initialValues) {
      updateStepResults(props.initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const { step, lastKnownValues } = values;
  const currentStep: any = getCurrentStep();

  const { validateAppointmentForm } = props;
  const validate = async (formValues) => {
    let errors;
    if (validateAppointmentForm) {
      await validateAppointmentForm(formValues, formValues.provider)
        .then(() => {
          errors = {};
        })
        .catch((e) => {
          errors = e;
        });

      return errors;
    }
    return {};
  };

  const setFieldTouched = (args: any[], state: MutableState<any, any>) => {
    const [name, touched] = args;
    const field = state.fields[name];

    if (field) {
      field.touched = !!touched;
    }
  };

  return (
    <Form
      initialValues={props.initialValues}
      validate={validate}
      validateOnBlur
      onSubmit={onSubmit}
      mutators={{
        setFieldTouched,
      }}
      render={(form: any) => {
        const wizard = createWizardController({ form });

        if (!isEqual(lastKnownValues, form.values)) {
          // updateSteps when form changes
          updateStepResults(form.values);
        }

        return (
          <Container
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            mb="md"
            minHeight="100%"
          >
            <ScrollIntoView key={step} />
            {React.cloneElement(currentStep, { wizard })}
          </Container>
        );
      }}
    />
  );
}

const SchedulingWizardComponent: any = compose(
  graphql(CREATE_APPOINTMENT_MUTATION, {
    name: 'createAppointment',
    alias: 'withCreateAppointment',
    options: { context: LM_CLIENT_CONTEXT },
  }),
  graphql(MARK_REQUEST_AS_SCHEDULES, {
    name: 'markScheduleRequestAttemptAsScheduled',
    alias: 'withMarkScheduleRequestAttemptAsScheduled',
    options: { context: DILM_CLIENT_CONTEXT },
  }),
  graphql(WIDGET_SESSION_SCHEDULED_MUTATION, {
    name: 'widgetSessionScheduled',
    alias: 'withWidgetSessionScheduled',
    options: { context: DILM_CLIENT_CONTEXT },
  })
)(SchedulingWizard);

export default SchedulingWizardComponent;
